<template>
    <!-- Outer takes Blade `class` (flex, min-w, …). Inner is the positioning context only — avoids flex/static-position bugs for the panel. -->
    <div class="header-language-selector-outer" v-bind="$attrs">
        <div v-show="ready" class="relative inline-block w-fit shrink-0" ref="rootRef">
        <button
            type="button"
            @click="open = !open"
            class="inline-flex items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50/80 px-3.5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-czech-blue/20 focus:ring-offset-1"
            :aria-expanded="open"
            aria-haspopup="listbox"
            aria-controls="header-lang-menu"
        >
            <img
                v-if="currentLanguage?.flag"
                :src="currentLanguage.flag"
                :alt="currentLanguageAlt"
                class="h-5 w-5 rounded-full object-cover ring-1 ring-gray-200/80 flex-shrink-0"
                width="20"
                height="20"
            />
            <span
                v-else
                class="h-5 w-5 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-600 flex-shrink-0"
            >
                {{ currentLanguageLabel.substring(0, 2).toUpperCase() }}
            </span>
            <span>{{ currentLanguageLabel }}</span>
            <svg
                class="h-4 w-4 text-gray-500 transition-transform duration-200"
                :class="{ 'rotate-180': open }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
            >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
        </button>
        <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <div
                v-if="open"
                id="header-lang-menu"
                role="listbox"
                class="absolute right-0 top-full z-[60] mt-2 min-w-[28rem] max-w-[calc(100vw-2rem)] origin-top-right rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5"
            >
                <div class="grid grid-cols-2 gap-x-1 gap-y-0.5">
                    <a
                        v-for="lang in visibleLanguages"
                        :key="lang.code"
                        :href="lang.path || '#'"
                        class="flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 rounded-lg"
                        :class="codeEq(currentLanguage?.code, lang.code) ? 'bg-czech-blue/5 text-czech-blue font-semibold' : ''"
                    >
                        <img
                            v-if="lang.flag"
                            :src="lang.flag"
                            :alt="languageAlt(lang)"
                            class="h-5 w-5 rounded-full object-cover ring-1 ring-gray-200/80 flex-shrink-0"
                            width="20"
                            height="20"
                        />
                        <span
                            v-else
                            class="h-5 w-5 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0"
                        >
                            {{ (lang.code || '').substring(0, 2).toUpperCase() }}
                        </span>
                        <span class="truncate">{{ lang.name }}</span>
                    </a>
                </div>
            </div>
        </Transition>
        </div>
        <div v-show="!ready">
            <slot />
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { normalizeCmsAssetUrl } from '../utils/cmsAssetUrl.js';

export default {
    name: 'HeaderLanguageSelector',
    inheritAttrs: false,
    props: {
        currentLang: { type: Object, default: null },
        pathsByLanguage: { type: Object, default: () => ({}) },
    },
    setup(props) {
        const store = useStore();
        const open = ref(false);
        const ready = ref(false);
        const rootRef = ref(null);
        const storeLanguages = computed(() => store.getters.languages || []);

        function codeEq(a, b) {
            return String(a ?? '').toUpperCase() === String(b ?? '').toUpperCase();
        }

        function withFlag(lang) {
            if (!lang || typeof lang !== 'object') return lang;
            const flag = normalizeCmsAssetUrl(lang.flag);
            return flag ? { ...lang, flag } : lang;
        }

        const currentLanguage = computed(() => {
            const currentCode = props.currentLang?.code;
            if (!currentCode) return withFlag(props.currentLang);

            const langs = storeLanguages.value;
            return withFlag(
                langs.find((lang) => codeEq(lang.code, currentCode))
                || props.currentLang
            );
        });

        const currentLanguageLabel = computed(() => {
            const lang = currentLanguage.value;
            return String(lang?.code_view
                ?? lang?.translated?.name
                ?? lang?.code
                ?? 'EN');
        });

        const currentLanguageAlt = computed(() => languageAlt(currentLanguage.value));

        function languageAlt(lang) {
            const label = String(lang?.translated?.name
                ?? lang?.name
                ?? lang?.code_view
                ?? lang?.code
                ?? 'Language');

            return `${label} flag`;
        }

        const visibleLanguages = computed(() => {
            const paths = props.pathsByLanguage || {};
            const langs = storeLanguages.value;

            const langMap = Object.fromEntries(
                langs.map((l) => [l.code, l])
            );
            const langByUpper = Object.fromEntries(
                langs
                    .filter((l) => l && l.code != null && l.code !== '')
                    .map((l) => [String(l.code).toUpperCase(), l])
            );

            return Object.entries(paths)
                .map(([code, path]) => {
                    const lang =
                        langMap[code]
                        ?? langByUpper[String(code).toUpperCase()];

                    return withFlag({
                        ...(lang || {}),
                        code,
                        path,
                        name:
                            lang?.translated?.name
                            ?? lang?.code_view
                            ?? code
                    });
                })
                .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
        });

        function onOutsideClick(e) {
            if (rootRef.value && !rootRef.value.contains(e.target)) {
                open.value = false;
            }
        }

        onMounted(() => {
            ready.value = true;
            document.addEventListener('click', onOutsideClick);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', onOutsideClick);
        });

        return {
            open,
            ready,
            rootRef,
            visibleLanguages,
            currentLanguage,
            currentLanguageLabel,
            currentLanguageAlt,
            languageAlt,
            codeEq,
        };
    },
};

</script>
